import {
  prisma,
  type Prisma,
  TaskTransitionType,
  InvitationStatus,
  UserRoleAssignmentEntityType,
  AvailabilitiesModel,
  User,
  FormEntityType,
  FormEntity,
  ContactBookGroupType,
  NetworkingModel,
  Sponsor,
  ParticipantGroupType,
  ParticipantType,
  NetworkingType,
} from "client";
import {
  createParticipantFormTemplate,
  transformSurveyResult,
} from "../formTemplates";
import { invalidateEventPageCacheForEvent } from "../event/eventPageCache";
import { adoptUnscopedUser } from "services/user/scopedUser";

function participantGroupTranslationName(
  group: {
    id: string;
    translations?: {
      name?: string | null;
      language?: { code?: string | null } | null;
    }[];
  },
  lang?: string
) {
  return (
    group.translations?.find((item) => item.language?.code === lang)?.name ??
    group.translations?.[0]?.name
  );
}

const enum Status {
  RESERVED = "Reserved",
  PENDING_APPROVAL = "Pending Approval",
  WAITING_FOR_PAYMENT = "Waiting For Payment",
}
import {
  OWNER_PORTAL_PERMISSIONS,
  cleanupCompanyTeamUsers,
} from "../team/owner";
import jwt from "jsonwebtoken";
import { createDefaultAvailabilities } from "../availability";
import { VALIDATION_RULES } from "@sajilni/config";
import {
  DEFAULT_QUESTIONS,
  toStorableFormQuestion,
} from "@sajilni/utils/formTemplate";
import { isMongoObjectId } from "@sajilni/utils/ticketQr";

import { getLanguages } from "services/language";
import { getContactBookGroupBySponsorId } from "../contactBookGroup";
import { createDefaultNetworkingSettings } from "../networkingSettings";
import { getWhiteLabelByEventId } from "services/white-label";
import {
  assertNotOwnerTeamMember,
  updatePortalTeamMember,
  syncCompanyAdminUserAndProfile,
  getCompanyAdminEmailConflict,
} from "services/team";
// READ
export const getSponsors = async (): Promise<Sponsor[]> => {
  return await prisma.sponsor.findMany();
};

export const updateSponsorSetting = async (
  data: Prisma.SponsorSettingsCreateInput & { eventId: string }
) => {
  if (data.id) {
    return await prisma.sponsorSettings.update({
      where: { id: data.id },
      data: {
        supportEmail: data.supportEmail,
        dashboardLogo: data.dashboardLogo,
        welcomeDescription: data.welcomeDescription,
      },
    });
  }
  return await prisma.sponsorSettings.create({
    data: {
      supportEmail: data.supportEmail,
      dashboardLogo: data.dashboardLogo,
      welcomeDescription: data.welcomeDescription,
      eventId: data.eventId,
    },
  });
};

export const getSponsorSetting = async (eventId: string) => {
  return await prisma.sponsorSettings.findUnique({
    where: { eventId },
  });
};

/** Indexed: Sponsor @@index([eventId, teamIds]). */
export const isUserOnSponsorTeam = async (userId: string, eventId: string) => {
  const row = await prisma.sponsor.findFirst({
    where: { eventId, teamIds: { has: userId } },
    select: { id: true },
  });
  return !!row;
};

/**
 * Membership via email + event slug.
 * Resolves User ids + Event.id first, then uses @@index([eventId, teamIds]).
 */
export const isUserSponsorInEvent = async (
  email: string,
  eventSlug: string
) => {
  const normalizedEmail = email.toLocaleLowerCase();
  const [users, event] = await Promise.all([
    prisma.user.findMany({
      where: { email: normalizedEmail },
      select: { id: true },
    }),
    prisma.event.findUnique({
      where: { slug: eventSlug },
      select: { id: true },
    }),
  ]);
  if (!event || users.length === 0) return false;

  const row = await prisma.sponsor.findFirst({
    where: {
      eventId: event.id,
      teamIds: { hasSome: users.map((u) => u.id) },
    },
    select: { id: true },
  });
  return !!row;
};
export const getSponsorByUserId = async (id: string, eventId: string) => {
  const userSponsor = await prisma.sponsor.findFirst({
    where: {
      team: {
        some: {
          id,
        },
      },
      event: {
        slug: eventId,
      },
    },
  });
  return userSponsor ?? false;
};
export const getSponsorByEmail = async (email: string, eventId: string) => {
  const userSponsor = await prisma.sponsor.findFirst({
    where: {
      email,
      event: {
        slug: eventId,
      },
    },
  });
  return userSponsor ?? false;
};
export const isSponsorTeamMember = async (eventId: string, email: string) => {
  const sponsor = await prisma.sponsor.findFirst({
    where: {
      event: {
        slug: eventId,
      },
      team: {
        some: {
          email,
        },
      },
      email: {
        not: email,
      },
    },
  });
  return !!sponsor?.id;
};
export const getSponsorById = async (
  id: string,
  lang?: string,
  options?: { includeParticipantGroup?: boolean; includeTranslations?: boolean }
) => {
  try {
    const sponsor = await prisma.sponsor.findUnique({
      where: { id },
      include: {
        ...(options?.includeParticipantGroup
          ? {
              participantGroup: {
                include: {
                  translations: {
                    include: {
                      language: true,
                    },
                  },
                },
              },
            }
          : {}),
        ...(options?.includeTranslations ? { translations: true } : {}),
        team: {
          include: {
            profiles: true,
          },
        },
      },
    });
    if (!sponsor) return null;
    const groupName = sponsor.participantGroup
      ? participantGroupTranslationName(sponsor.participantGroup, lang)
      : undefined;

    return {
      ...sponsor,
      ...(sponsor.participantGroup !== undefined
        ? {
            participantGroup: sponsor.participantGroup
              ? { ...sponsor.participantGroup, name: groupName }
              : null,
          }
        : {}),
      contactName: sponsor?.team
        ?.find((team) => team.email === sponsor?.email)
        ?.profiles?.find((profile) => profile.eventId === sponsor?.eventId)
        ?.name,
    };
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getSponsorByEventSlugAndUserId = async (
  slug: string,
  userId: string,
  eventId: string,
  lang?: string,
  includeTasks = true
) => {
  // Profile.eventId is @db.ObjectId — never pass cuid slug there.
  const profileEventWhere = isMongoObjectId(eventId)
    ? { eventId }
    : { event: { slug } };

  const res = await prisma.sponsor.findFirst({
    where: {
      event: {
        slug,
      },
      OR: [{ id: userId }, { team: { some: { id: userId } } }],
    },
    include: {
      translations: {
        include: {
          language: true,
        },
      },
      team: {
        include: {
          profiles: {
            where: profileEventWhere,
            orderBy: {
              createdAt: "desc",
            },
          },
        },
      },
      participantGroup: {
        include: {
          translations: {
            include: {
              language: true,
            },
          },
        },
      },
      networking: true,
      // Public networking profiles don't need nested task/form trees.
      ...(includeTasks
        ? {
            taskEntities: {
              where: {
                task: {
                  OR: [
                    // Show all non-hidden tasks
                    {
                      hide: false,
                    },
                    // Show hidden tasks if due date has passed
                    {
                      AND: [
                        {
                          hide: true,
                          dueDate: {
                            lt: new Date(),
                          },
                        },
                      ],
                    },
                  ],
                },
              },
              include: {
                taskTransitions: true,
                formResults: {
                  include: {
                    formResult: true,
                  },
                },
                task: {
                  include: {
                    formEntity: {
                      include: {
                        formTemplate: true,
                      },
                    },
                    taskTranslations: {
                      where: {
                        language: {
                          code: lang,
                        },
                      },
                    },
                  },
                },
              },
            },
          }
        : {}),
    },
  });
  if (!res) return null;

  const langData =
    res.translations?.find((item) => item.language.code === lang) ??
    res.translations?.[0];
  const groupName =
    res.participantGroup?.translations?.find(
      (item) => item.language.code === lang
    )?.name ?? res.participantGroup?.translations?.[0]?.name;

  return {
    ...res,
    name: langData?.name,
    bio: langData?.bio,
    tags: langData?.tags,
    participantGroup: res.participantGroup
      ? { ...res.participantGroup, name: groupName }
      : null,
    team: [
      ...new Map(
        (res.team ?? []).map((member) => [member.id, member])
      ).values(),
    ].map((team) => {
      const profile = team?.profiles?.find((p) => p.eventId === eventId);
      return {
        ...team,
        ...profile,
        id: team?.id,
        name: profile?.name || team?.name,
        // Prefer person photo; never let null profile.image wipe user.image;
        // fall back to company logo so Team tab matches the profile header.
        image: profile?.image || team?.image || res.logo || null,
      };
    }),
    taskEntities: (() => {
      if (
        !includeTasks ||
        !("taskEntities" in res) ||
        !Array.isArray(res.taskEntities)
      ) {
        return [];
      }
      type NestedTaskEntity = {
        task?: {
          dueDate: Date;
          disableAfterDue?: boolean | null;
          formEntity?: {
            formTemplate?: { json?: unknown } | null;
          } | null;
        } | null;
        formResults?: Array<{
          formResult?: {
            surveyResult?: unknown;
          } | null;
        }>;
      };
      return (res.taskEntities as NestedTaskEntity[]).map((taskEntity) => ({
        ...taskEntity,
        stopAcceptingSubmissions:
          !!taskEntity.task &&
          taskEntity.task.dueDate < new Date() &&
          taskEntity.task.disableAfterDue === true,
        formResult: taskEntity.formResults?.[0]?.formResult
          ? {
              ...taskEntity.formResults[0].formResult,
              surveyResult: transformSurveyResult(
                taskEntity.task?.formEntity?.formTemplate?.json,
                taskEntity.formResults[0].formResult?.surveyResult
              ),
            }
          : null,
      }));
    })(),
  };
};

export const getEventSponsorsWithNoInvitation = async (eventId: string) => {
  const sponsors = await prisma.sponsor.findMany({
    where: {
      eventId,
      teamIds: { isEmpty: false },
      sponsorInvitations: {
        none: {},
      },
      participantGroupId: {
        not: null,
      },
    },
    include: {
      team: {
        include: {
          profiles: true,
        },
      },
    },
  });
  return sponsors.map((sponsor) => ({
    ...sponsor,
    contactName: sponsor?.team
      ?.find((team) => team.email === sponsor?.email)
      ?.profiles?.find((profile) => profile.eventId === sponsor?.eventId)?.name,
  }));
};

export const getSponsorsByEventId = async (
  eventId: string,
  lang?: string,
  options?: {
    includeParticipantGroup?: boolean;
  }
) => {
  const sponsor = await prisma.sponsor.findMany({
    where: {
      eventId,
    },
    include: {
      ...(options?.includeParticipantGroup
        ? {
            participantGroup: {
              include: {
                translations: {
                  include: {
                    language: true,
                  },
                },
              },
            },
          }
        : {}),
      taskEntities: {
        include: {
          taskTransitions: true,
        },
      },
      sponsorInvitations: true,
      team: {
        include: {
          profiles: {
            where: {
              eventId: eventId,
            },
          },
        },
      },
      translations: lang
        ? {
            where: {
              language: {
                code: lang,
              },
            },
          }
        : true,
    },
  });
  return sponsor.map((sponsor: any) => {
    const groupName = sponsor.participantGroup
      ? participantGroupTranslationName(sponsor.participantGroup, lang)
      : undefined;

    return {
      ...sponsor,
      name: sponsor.translations?.[0]?.name,
      bio: sponsor.translations?.[0]?.bio,
      ...(sponsor.participantGroup !== undefined
        ? {
            participantGroup: sponsor.participantGroup
              ? { ...sponsor.participantGroup, name: groupName }
              : null,
          }
        : {}),
      contactName: sponsor.team?.profiles?.[0]?.name,
      contactJobTitle: sponsor.team?.profiles?.[0]?.jobTitle,
      contactPhone: sponsor.team?.profiles?.[0]?.phoneNumber,
      completedTasksPercentage: {
        total: sponsor.taskEntities.length,
        completed: sponsor.taskEntities.filter(
          (task: any) =>
            task.taskTransitions[task.taskTransitions.length - 1]?.toState ===
              TaskTransitionType.COMPLETED ||
            task.taskTransitions[task.taskTransitions.length - 1]?.toState ===
              TaskTransitionType.SUBMITTED
        ).length,
      },
    };
  });
};

export const getSponsorsInvitationByEventId = async (eventId: string) => {
  const totalInvitation = await prisma.participantInvitation.count({
    where: {
      sponsor: {
        eventId,
      },
      entityType: ParticipantType.SPONSOR,
    },
  });
  const totalAccepted = await prisma.participantInvitation.count({
    where: {
      sponsor: {
        eventId,
      },
      entityType: ParticipantType.SPONSOR,
      status: InvitationStatus.ACCEPTED,
    },
  });
  return {
    totalInvitation,
    totalAccepted,
  };
};

export const getSponsorsByEventIdDatatable = async (
  eventId: string,
  pageNumber: number = 1,
  pageSize: number = 10,
  searchByName?: string,
  lang?: string,
  orderBy?: {
    [key: string]: "asc" | "desc";
  }
) => {
  try {
    // Calculate offset for pagination
    const skip = (pageNumber - 1) * pageSize;
    const escapeRegex = (str: string) =>
      str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // Escapes regex special characters

    const searchParam = searchByName ? escapeRegex(searchByName) : "";
    const filter = searchParam
      ? {
          OR: [
            { email: { contains: searchParam, mode: "insensitive" } },
            { boothName: { contains: searchParam, mode: "insensitive" } },
            {
              translations: {
                some: {
                  OR: [
                    { name: { contains: searchParam, mode: "insensitive" } },
                    { bio: { contains: searchParam, mode: "insensitive" } },
                  ],
                },
              },
            },
            {
              team: {
                some: {
                  profiles: {
                    some: {
                      OR: [
                        {
                          name: { contains: searchParam, mode: "insensitive" },
                        },
                        {
                          phoneNumber: {
                            contains: searchParam,
                            mode: "insensitive",
                          },
                        },
                      ],
                    },
                  },
                },
              },
            },
          ],
        }
      : {};
    // Fetch sponsors from the database
    const sponsors = (await prisma.sponsor.findMany({
      // @ts-ignore
      where: {
        teamIds: { isEmpty: false },
        eventId,
        ...filter,
      },
      include: {
        participantGroup: {
          include: {
            translations: {
              include: {
                language: true,
              },
            },
          },
        },
        team: {
          include: {
            profiles: {
              where: {
                eventId: eventId,
              },
            },
          },
        },
        taskEntities: {
          include: {
            formResults: {
              include: {
                formResult: true,
              },
            },
            taskTransitions: true,
            task: {
              include: {
                formEntity: {
                  include: {
                    formTemplate: true,
                  },
                },
                taskTranslations: {
                  where: {
                    language: {
                      code: lang,
                    },
                  },
                },
              },
            },
          },
        },
        sponsorInvitations: true,
        translations: lang
          ? {
              where: {
                language: {
                  code: lang,
                },
              },
            }
          : true,
      },
      skip,
      take: pageSize,
      orderBy: orderBy || {
        createdAt: "desc",
        // Order by creation date or any other field
      },
    })) as any;
    // Count total sponsors for the given eventId and search filter
    const totalSponsors = await prisma.sponsor.count({
      // @ts-ignore
      where: {
        teamIds: { isEmpty: false },
        eventId,
        ...filter,
      },
    });
    // Return the data table with pagination info
    return {
      response: sponsors.map((sponsor: any) => {
        const groupName =
          sponsor.participantGroup?.translations?.find(
            (item: any) => item.language?.code === lang
          )?.name ?? sponsor.participantGroup?.translations?.[0]?.name;

        return {
          ...sponsor,
          name: sponsor.translations?.[0]?.name,
          bio: sponsor.translations?.[0]?.bio,
          participantGroup: sponsor.participantGroup
            ? { ...sponsor.participantGroup, name: groupName }
            : null,
          contactName: sponsor.team?.find(
            (team: any) => team.email === sponsor.email
          )?.profiles?.[0]?.name,
          contactJobTitle: sponsor.team?.find(
            (team: any) => team.email === sponsor.email
          )?.profiles?.[0]?.jobTitle,
          contactPhone: sponsor.team?.find(
            (team: any) => team.email === sponsor.email
          )?.profiles?.[0]?.phoneNumber,
          completedTasksPercentage: {
            total: sponsor.taskEntities.length,
            completed: sponsor.taskEntities.filter(
              (task: any) =>
                task.taskTransitions[task.taskTransitions.length - 1]
                  ?.toState === TaskTransitionType.COMPLETED ||
                task.taskTransitions[task.taskTransitions.length - 1]
                  ?.toState === TaskTransitionType.SUBMITTED
            ).length,
          },
          taskEntities: sponsor.taskEntities.map((task: any) => {
            return {
              ...task,
              timeSubmitted:
                task.taskTransitions[task.taskTransitions.length - 1]
                  ?.createdAt || null,
              formResult: task.formResults?.[0]?.formResult
                ? {
                    ...task.formResults?.[0]?.formResult,
                    surveyResult: transformSurveyResult(
                      task.task.formEntity.formTemplate.json,
                      task.formResults?.[0]?.formResult.surveyResult
                    ),
                  }
                : null,
              task: {
                ...task.task,
                name: task.task.taskTranslations?.[0]?.name,
              },
            };
          }),
          hasUploadedFiles: sponsor.taskEntities.some(
            (task: any) =>
              task.uploadedFiles.length > 0 || task.formResults?.[0]?.formResult
          ),
        };
      }),

      pagination: {
        pageNumber,
        pageSize,
        total: totalSponsors,
        totalPages: Math.ceil(totalSponsors / pageSize),
        hasNextPage: totalSponsors > pageNumber * pageSize,
      },
    };
  } catch (error) {
    console.log(error);
    return error;
  }
};

export const getSponsorTasks = async (
  entityID: string,
  pageNumber: number = 1,
  pageSize: number = 10,
  searchByName?: string
) => {
  const skip = (pageNumber - 1) * pageSize;
  const escapeRegex = (str: string) =>
    str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // Escapes regex special characters

  const searchParam = searchByName ? escapeRegex(searchByName) : "";

  const filter: any = searchParam
    ? {
        OR: [
          {
            task: {
              taskTranslations: {
                some: {
                  OR: [
                    { name: { contains: searchParam, mode: "insensitive" } },
                  ],
                },
              },
            },
          },
        ],
      }
    : {};

  try {
    const tasks = await prisma.taskEntity.findMany({
      where: {
        entityID: entityID,
        ...filter,
      },
      include: {
        task: {
          include: {
            formEntity: {
              include: {
                formTemplate: true,
              },
            },
            taskTranslations: true,
          },
        },
        taskTransitions: true,
        formResults: {
          include: {
            formResult: true,
          },
        },
      },
      skip,
      take: pageSize,
      orderBy: {
        createdAt: "desc",
      },
    });
    const totalTasks = await prisma.taskEntity.count({
      where: {
        entityID,
        ...filter,
      },
    });
    return {
      data: tasks.map((task: any) => ({
        ...task,
        name: task.task.taskTranslations?.[0]?.name,
        timeSubmitted:
          task.taskTransitions[task.taskTransitions.length - 1]?.createdAt ||
          null,
        formResult: task.formResults[0]?.formResult
          ? {
              ...task.formResults[0].formResult,
              surveyResult: transformSurveyResult(
                task.task.formEntity.formTemplate.json,
                task.formResults[0].formResult.surveyResult
              ),
            }
          : null,
      })),
      pagination: {
        pageNumber,
        pageSize,
        total: totalTasks,
        totalPages: Math.ceil(totalTasks / pageSize),
        hasNextPage: totalTasks > pageNumber * pageSize,
      },
    };
  } catch (error) {
    console.log(error);
    return error;
  }
};
// CREATE
export const createSponsor = async (
  data: Prisma.SponsorCreateInput & {
    phone: string;
    contactName: string;
    contactJobTitle?: string;
    sector?: string | null;
    country?: string | null;
    company?: string | null;
    qrReferenceNumber?: string | null;
  },
  eventId: string,
  translations: Prisma.SponsorTranslationsCreateManySponsorInput[],
  whiteLabelId: string,
  participantGroupId?: string,
  session?: User,
  orderItemId?: string,
  participantImportId?: string
) => {
  try {
    const languages = await getLanguages();

    const jsonData = {
      title: "",
      logoPosition: "",
      pages: [
        {
          name: "page1",
          elements: Object.keys(data).map((fieldName) => {
            const existingQuestion = DEFAULT_QUESTIONS.find(
              (q) => q.name === fieldName
            );
            const element = existingQuestion ?? {
              type: "text",
              name: fieldName,
              title: fieldName,
            };
            return toStorableFormQuestion(
              element as Record<string, unknown>
            ) as typeof element;
          }),
        },
      ],
    };
    const sponsorSuperAdminRole = await prisma.role.findFirst({
      where: {
        name: "SPONSOR_SUPER_ADMIN",
      },
    });
    // check if sponsor name is exist in the same event
    const sponsorNameExist = await prisma.sponsor.findFirst({
      where: {
        eventId: eventId,
        translations: {
          some: {
            name: translations[0].name,
          },
        },
      },
    });
    if (sponsorNameExist) {
      return {
        error: "Sponsor name is already exist in the same event",
      };
    }

    // check if sponsor  email is exist in the same event
    const sponsorEmailExist = await prisma.sponsor.findFirst({
      where: {
        email: data.email,
        eventId: eventId,
      },
    });
    if (sponsorEmailExist) {
      return {
        error: "Sponsor email is already exist in the same event",
      };
    }
    let participantGroup;
    if (participantGroupId) {
      participantGroup = await prisma.participantGroup.findUnique({
        where: {
          id: participantGroupId,
        },
        include: {
          tasks: true,
        },
      });
    }

    const contactBookGroup = await prisma.contactBookGroup.create({
      data: {
        type: ContactBookGroupType.SPONSOR,
        name: `${ContactBookGroupType.SPONSOR} ${
          translations?.[0]?.name ?? ""
        }`,
        fields: {
          create: {
            type: FormEntityType.CONTACT_BOOK_GROUP,
            formTemplate: {
              create: {
                translations: {
                  create: languages.map((lang) => ({
                    name: `${
                      translations.find((t) => t.languageId === lang.id)
                        ?.name ?? translations[0]?.name
                    } Form`,
                    language: { connect: { id: lang.id } },
                  })),
                },
                whiteLabelId,
                // createdById is optional — session.id may be auth sub, not a User row.
                json: jsonData as Prisma.InputJsonValue,
                text: JSON.stringify(jsonData),
                numberOfQuestions: Object.keys(jsonData.pages[0].elements)
                  .length,
              },
            },
          },
        },
        event: { connect: { id: eventId } },
        participantImportId,
      },
    });
    const {
      contactName,
      phone,
      contactJobTitle,
      sector,
      country,
      company,
      qrReferenceNumber,
      ...rest
    } = data;
    const normalizedEmail = data.email?.toLocaleLowerCase() ?? "";
    const ownerProfilePermissions = participantGroupId
      ? {
          adminSettings: true,
          companySettings: true,
          eventDayBadge: false,
        }
      : OWNER_PORTAL_PERMISSIONS;

    if (!whiteLabelId) {
      throw new Error("White label is required to create a user");
    }
    await adoptUnscopedUser(normalizedEmail, whiteLabelId);
    const user = await prisma.user.upsert({
      where: {
        email_accessibleWhiteLabelId: {
          email: normalizedEmail,
          accessibleWhiteLabelId: whiteLabelId,
        },
      },
      update: {
        ...(phone ? { phoneNumber: phone } : {}),
      },
      create: {
        name: contactName || translations[0]?.name,
        phoneNumber: phone,
        email: normalizedEmail,
        accessibleWhiteLabelId: whiteLabelId as string,
        participantImportId,
        profiles: {
          create: {
            email: normalizedEmail,
            name: contactName || translations[0]?.name,
            phoneNumber: phone,
            qrReferenceNumber,
            eventId,
            jobTitle: contactJobTitle,
            company: company || translations[0]?.name,
            country: country || undefined,
            sector: sector || undefined,
            bio: translations[0]?.bio,
            contactBookGroupId: contactBookGroup.id,
            exhibitorPermissions: ownerProfilePermissions,
            participantImportId,
          },
        },
        userRoleAssignments: {
          create: {
            entityType: UserRoleAssignmentEntityType.SPONSOR,
            roleId: sponsorSuperAdminRole?.id,
          },
        },
      },
    });

    const ownerProfile = await prisma.profile.findFirst({
      where: { userId: user.id, eventId },
      select: { id: true },
    });
    if (!ownerProfile) {
      await prisma.profile.create({
        data: {
          userId: user.id,
          email: normalizedEmail,
          name: contactName || translations[0]?.name,
          phoneNumber: phone,
          qrReferenceNumber,
          eventId,
          jobTitle: contactJobTitle,
          company: company || translations[0]?.name,
          country: country || undefined,
          sector: sector || undefined,
          bio: translations[0]?.bio,
          contactBookGroupId: contactBookGroup.id,
          exhibitorPermissions: ownerProfilePermissions,
          participantImportId,
        },
      });
    } else {
      await prisma.profile.update({
        where: { id: ownerProfile.id },
        data: {
          exhibitorPermissions: ownerProfilePermissions,
          name: contactName || translations[0]?.name,
          email: normalizedEmail,
          qrReferenceNumber,
          company: company || translations[0]?.name,
          jobTitle: contactJobTitle,
          bio: translations[0]?.bio,
          ...(country ? { country } : {}),
          ...(sector ? { sector } : {}),
          ...(phone ? { phoneNumber: phone } : {}),
          participantImportId,
        },
      });
    }

    const response = await prisma.sponsor.create({
      data: {
        ...rest,
        participantImportId,
        email: normalizedEmail || rest.email,
        event: {
          connect: {
            id: eventId,
          },
        },

        participantGroup: participantGroupId
          ? { connect: { id: participantGroupId } }
          : undefined,
        contactBookGroups: { connect: { id: contactBookGroup.id } },
        orderItems: orderItemId ? { connect: { id: orderItemId } } : undefined,
        team: { connect: { id: user.id } },
        translations: {
          createMany: {
            data: translations.map((translation) => ({
              languageId: translation.languageId,
              name: translation.name,
              contactName: translation.contactName,
              bio: translation.bio,
              tags: translation.tags,
              adminNotes: translation.adminNotes,
              description: translation.description,
            })),
          },
        },
      },
      include: {
        team: true,
      },
    });
    await prisma.assignmentModel.upsert({
      where: {
        userId_entityType_entityId: {
          userId: user.id,
          entityType: UserRoleAssignmentEntityType.SPONSOR,
          entityId: response.id,
        },
      },
      create: {
        userId: user.id,
        entityType: UserRoleAssignmentEntityType.SPONSOR,
        roleId: sponsorSuperAdminRole?.id,
        entityId: response.id,
      },
      update: {
        roleId: sponsorSuperAdminRole?.id,
      },
    });

    //defaults for Sponsor company
    await createDefaultAvailabilities({
      entityId: response.id,
      entityModel: AvailabilitiesModel.SPONSOR,
      eventId: eventId,
    });
    await createDefaultNetworkingSettings(
      eventId,
      response.id,
      NetworkingModel.SPONSOR,
      user,
      undefined, // participantGroupId - not needed here
      NetworkingType.BY_REQUEST // type
    );
    const FormTranslations = translations?.map((t) => ({
      name: t?.name || "",
      languageId: t?.languageId,
    }));

    await createParticipantFormTemplate(
      {
        whiteLabelId: whiteLabelId,
        eventId: eventId,
        createdById: user.id,
      },
      FormTranslations as any,
      {
        json: {
          logoPosition: "right",
          pages: [
            {
              name: "page1",
              elements: [
                {
                  type: "dropdown",
                  name: "question1",
                  title: "Meeting Location",
                  choices: [
                    {
                      value: "Item 1",
                      text: "Coffee Area",
                    },
                    {
                      value: "Item 2",
                      text: "Booth",
                    },
                    {
                      value: "Item 3",
                      text: "Networking Area",
                    },
                    {
                      value: "Item 4",
                      text: "Other",
                    },
                  ],
                },
                {
                  type: "text",
                  name: "question4",
                  visibleIf: "{question1} = 'Item 4'",
                  title: "Custom Location",
                },
                {
                  type: "dropdown",
                  name: "question2",
                  title: "Reason",
                  choices: [
                    {
                      value: "Item 1",
                      text: "Need a mentoring consultation",
                    },
                    {
                      value: "Item 2",
                      text: "Chat and idea exchange",
                    },
                  ],
                },
                {
                  type: "comment",
                  name: "question3",
                  title: "Description",
                },
              ],
            },
          ],
        },
        numberOfQuestions: 4,
      } as any,
      {
        entityID: response?.id,
        type: FormEntityType.MEETING,
      } as FormEntity
      // entities
    );
    //defaults for User admin
    await createDefaultAvailabilities({
      entityId: user.id,
      entityModel: AvailabilitiesModel.USER,
      eventId: eventId,
    });
    await createDefaultNetworkingSettings(
      eventId,
      user.id,
      NetworkingModel.USER,
      user,
      undefined, // participantGroupId - not needed here
      NetworkingType.BY_REQUEST // type
    );
    const userFormTranslations = translations?.map((t) => ({
      name: user?.name || "",
      languageId: t?.languageId,
    }));

    const invitationEmail =
      normalizedEmail || response.email?.toLocaleLowerCase() || "";
    const token = invitationEmail
      ? jwt.sign(
          {
            entityId: response.id,
            email: invitationEmail,
            entityType: ParticipantType.SPONSOR,
          },
          process.env.SECRET_HASH_PASSWORD as string
        )
      : undefined;

    if (token && invitationEmail) {
      await prisma.participantInvitation.upsert({
        where: {
          entityId_entityType_email: {
            entityId: response.id,
            entityType: ParticipantType.SPONSOR,
            email: invitationEmail,
          },
        },
        update: {
          token,
          userId: user.id,
          participantImportId,
        },
        create: {
          email: invitationEmail,
          status: InvitationStatus.PENDING,
          entityId: response.id,
          entityType: ParticipantType.SPONSOR,
          token,
          isOwner: true,
          userId: user.id,
          participantImportId,
        },
      });
    }
    // create task entities for the exhibitor
    if (participantGroupId && participantGroup) {
      await Promise.all(
        (participantGroup?.tasks || []).map(async (task) => {
          if (!task?.taskId) {
            console.error(`Task ID is missing for task:`, task);
            return;
          }

          return await prisma.taskEntity.create({
            data: {
              task: {
                connect: {
                  id: task.taskId,
                },
              },
              sponsor: {
                connect: {
                  id: response.id,
                },
              },
            },
          });
        })
      );
      //defaults for Sponsor company
      await createDefaultAvailabilities({
        entityId: response.id,
        entityModel: AvailabilitiesModel.SPONSOR,
        eventId: eventId,
      });
      await createDefaultNetworkingSettings(
        eventId,
        response.id,
        NetworkingModel.SPONSOR,
        user,
        participantGroupId
      );
      const FormTranslations = translations?.map((t) => ({
        name: t?.name || "",
        languageId: t?.languageId,
      }));

      await createParticipantFormTemplate(
        {
          whiteLabelId: whiteLabelId,
          eventId: eventId,
          createdById: user.id,
        },
        FormTranslations as any,
        {
          json: {
            logoPosition: "right",
            pages: [
              {
                name: "page1",
                elements: [
                  {
                    type: "dropdown",
                    name: "question1",
                    title: "Meeting Location",
                    choices: [
                      {
                        value: "Item 1",
                        text: "Coffee Area",
                      },
                      {
                        value: "Item 2",
                        text: "Booth",
                      },
                      {
                        value: "Item 3",
                        text: "Networking Area",
                      },
                      {
                        value: "Item 4",
                        text: "Other",
                      },
                    ],
                  },
                  {
                    type: "text",
                    name: "question4",
                    visibleIf: "{question1} = 'Item 4'",
                    title: "Custom Location",
                  },
                  {
                    type: "dropdown",
                    name: "question2",
                    title: "Reason",
                    choices: [
                      {
                        value: "Item 1",
                        text: "Need a mentoring consultation",
                      },
                      {
                        value: "Item 2",
                        text: "Chat and idea exchange",
                      },
                    ],
                  },
                  {
                    type: "comment",
                    name: "question3",
                    title: "Description",
                  },
                ],
              },
            ],
          },
          numberOfQuestions: 4,
        } as any,
        {
          entityID: response?.id,
          type: FormEntityType.MEETING,
        } as FormEntity
        // entities
      );
      //defaults for User admin
      await createDefaultAvailabilities({
        entityId: user.id,
        entityModel: AvailabilitiesModel.USER,
        eventId: eventId,
      });
      await createDefaultNetworkingSettings(
        eventId,
        user.id,
        NetworkingModel.USER,
        user,
        participantGroupId
      );
    }

    await invalidateEventPageCacheForEvent(eventId);

    return { ...response, token };
  } catch (error: any) {
    console.log(error);
    throw new Error(error.message);
  }
};

// get all sponsors that do not have a task entities with  task transition or the last task transition is new
export const getSponsorsWithNoSubmittedTasks = async () => {
  const sponsors = await prisma.sponsor.findMany({
    where: {
      taskEntities: {
        some: {
          OR: [
            {
              taskTransitions: {
                none: {},
              },
            },
            {
              taskTransitions: {
                some: {
                  toState: TaskTransitionType.NEW,
                },
              },
            },
          ],
        },
      },
    },
    include: {
      event: {
        include: {
          translations: true,
          whiteLabel: true,
        },
      },
      taskEntities: {
        include: {
          taskTransitions: true,
          task: true,
        },
      },
    },
  });
  return sponsors.map((sponsor) => ({
    ...sponsor,
    numberOfUncompletedTasks: sponsor.taskEntities.filter(
      (taskEntity) =>
        taskEntity.taskTransitions.length === 0 ||
        taskEntity.taskTransitions[taskEntity.taskTransitions.length - 1]
          .toState === TaskTransitionType.NEW
    ).length,
  }));
};

// UPDATE
export const updateSponsor = async (
  id: string,
  data: Prisma.SponsorCreateInput & {
    contactName?: string;
    contactJobTitle?: string;
    phone?: string;
    sector?: string | null;
    country?: string | null;
    company?: string | null;
  },
  translations: Prisma.SponsorTranslationsCreateManySponsorInput[],

  participantGroupId?: string
) => {
  try {
    const participantGroup = participantGroupId
      ? await prisma.participantGroup.findUnique({
          where: {
            id: participantGroupId,
          },
          include: {
            tasks: true,
          },
        })
      : null;
    //   is data.email different from the old email
    let isNewEmail = false;
    const sponsor = await prisma.sponsor.findUnique({
      where: {
        id,
      },
      select: {
        id: true,
        email: true,
        eventId: true,
        participantGroupId: true,
        teamIds: true,
        event: { select: { whiteLabelId: true } },
      },
    });

    if (data.email !== sponsor?.email) {
      isNewEmail = true;
      const sponsorEmailExist = await prisma.sponsor.findFirst({
        where: {
          email: data.email,
          eventId: sponsor?.eventId,
        },
      });
      if (sponsorEmailExist) {
        return {
          error: "Sponsor email is already exist in the same event",
        };
      }
      const contactEmailConflict = await getCompanyAdminEmailConflict({
        teamIds: sponsor?.teamIds ?? [],
        whiteLabelId: sponsor?.event?.whiteLabelId,
        previousEmail: sponsor?.email,
        email: typeof data.email === "string" ? data.email : null,
      });
      if (contactEmailConflict) {
        return { error: contactEmailConflict };
      }
    }
    const isOldParticipantGroup =
      (participantGroupId ?? null) === (sponsor?.participantGroupId ?? null);
    // check if sponsor name is exist in the same event
    const sponsorNameExist = await prisma.sponsor.findFirst({
      where: {
        eventId: sponsor?.eventId,
        translations: {
          some: {
            name: translations[0].name,
          },
        },
        NOT: {
          id,
        },
      },
    });
    if (sponsorNameExist) {
      return {
        error: "Sponsor name is already exist in the same event",
      };
    }
    const {
      contactName,
      contactJobTitle,
      phone,
      sector,
      country,
      company,
      ...rest
    } = data;
    const resolvedContactName =
      [contactName, translations?.[0]?.contactName]
        .find((value) => typeof value === "string" && value.trim())
        ?.trim() || "";
    const response = await prisma.sponsor.update({
      where: {
        id,
      },
      data: {
        ...rest,
        phone,
        participantGroup: participantGroupId
          ? { connect: { id: participantGroupId } }
          : { disconnect: true },
        taskEntities: !isOldParticipantGroup
          ? {
              deleteMany: {},
            }
          : {},
        ...(translations.length > 0
          ? {
              translations: {
                deleteMany: {},
                createMany: {
                  data: translations,
                },
              },
            }
          : {}),
        // Drop the search blob so the next company search rebuilds it from
        // the saved name and email. boothName is still not searched.
        searchCards: [],
      },
    });

    await syncCompanyAdminUserAndProfile({
      eventId: response.eventId,
      teamIds: sponsor?.teamIds ?? [],
      whiteLabelId: sponsor?.event?.whiteLabelId,
      previousEmail: sponsor?.email,
      email: response.email,
      name: resolvedContactName || undefined,
      phone,
      jobTitle: contactJobTitle,
      sector,
      country,
      company,
      socialMedia: {
        website: response.website,
        facebook: response.facebook,
        linkedin: response.linkedin,
        twitter: response.x,
        instagram: response.instagram,
      },
    });
    // create task entities for the exhibitor
    (participantGroup?.tasks || []).forEach(async (task) => {
      if (!task.taskId) {
        console.error(`Task ID is missing for task:`, task);
        return;
      }

      return await prisma.taskEntity.upsert({
        where: {
          taskId_entityID: {
            taskId: task.taskId,
            entityID: response.id,
          },
        },
        update: {
          task: {
            connect: {
              id: task.taskId,
            },
          },
          sponsor: {
            connect: {
              id: id,
            },
          },
        },
        create: {
          task: {
            connect: {
              id: task.taskId,
            },
          },
          sponsor: {
            connect: {
              id: id,
            },
          },
        },
      });
    });

    // Meeting emails + magic links look up ParticipantInvitation by entityId+email.
    // Re-sign JWT so payload.email matches the new address (updateMany alone leaves stale claims).
    if (isNewEmail && sponsor?.email && response.email) {
      const email = response.email;
      const invitations = await prisma.participantInvitation.findMany({
        where: {
          entityId: id,
          email: sponsor.email,
          entityType: ParticipantType.SPONSOR,
        },
      });
      const secret = process.env.SECRET_HASH_PASSWORD as string;
      await Promise.all(
        invitations.map((invitation) => {
          const token = jwt.sign(
            {
              entityId: id,
              email,
              entityType: ParticipantType.SPONSOR,
            },
            secret
          );
          return prisma.participantInvitation.update({
            where: { id: invitation.id },
            data: { email, token },
          });
        })
      );
    }

    if (sponsor?.eventId) {
      await invalidateEventPageCacheForEvent(sponsor.eventId);
    }

    return {
      ...response,
      isNewEmail,
    };
  } catch (error: any) {
    console.log("error", error);
    if (error.code == "P2014") {
      throw new Error(
        "Unable to update sponsor because it would violate the submitted tasks."
      );
    } else {
      throw new Error("An unexpected error occurred while updating sponsor.");
    }
  }
};

export const upsertSponsorInvitationState = async (
  sponsor: {
    id: string;
    email: string;
  },
  status: InvitationStatus
) => {
  try {
    const token = jwt.sign(
      {
        entityId: sponsor?.id,
        email: sponsor.email,
        entityType: ParticipantGroupType.SPONSORS,
      },
      process.env.SECRET_HASH_PASSWORD as string
    );
    const response = await prisma.participantInvitation.upsert({
      where: {
        entityId_entityType_email: {
          entityId: sponsor?.id,
          email: sponsor.email,
          entityType: ParticipantType.SPONSOR,
        },
      },
      update: {
        status: status,
      },
      create: {
        email: sponsor.email,
        status: status,
        entityId: sponsor.id,
        entityType: ParticipantType.SPONSOR,
        token,
      },
    });

    return response;
  } catch (error: any) {
    console.log(error);
    throw new Error(error.message);
  }
};

export const updateSponsorInvitation = async (
  sponsors: {
    id: string;
    email: string;
  }[],
  status: InvitationStatus
) => {
  try {
    const response = await prisma.participantInvitation.createMany({
      data: sponsors.map((sponsor) => {
        const token = jwt.sign(
          {
            entityId: sponsor?.id,
            email: sponsor.email,
            entityType: ParticipantType.SPONSOR,
          },
          process.env.SECRET_HASH_PASSWORD as string
        );
        return {
          email: sponsor.email,
          status: status,
          entityId: sponsor.id,
          token,
          isOwner: true,
          entityType: ParticipantType.SPONSOR,
        };
      }),
    });

    return response;
  } catch (error: any) {
    console.log(error);
    throw new Error(error.message);
  }
};
// DELETE
export const deleteSponsor = async (id: string) => {
  try {
    const sponsor = await prisma.sponsor.findUnique({
      where: { id },
      select: {
        id: true,
        email: true,
        eventId: true,
        teamIds: true,
        participantGroupId: true,
        event: { select: { whiteLabelId: true } },
      },
    });
    if (!sponsor) return true;

    await prisma.$transaction(
      async (tx) => {
        const taskEntitiesPromise = tx.taskEntity.findMany({
          where: { entityID: id },
          select: { id: true },
        });

        await cleanupCompanyTeamUsers(tx, {
          teamUserIds: sponsor.teamIds,
          eventId: sponsor.eventId,
          participantGroupId: sponsor.participantGroupId,
          companyEntityId: id,
          entityType: UserRoleAssignmentEntityType.SPONSOR,
        });

        const taskEntities = await taskEntitiesPromise;
        const taskIds = taskEntities.map((t) => t.id);

        await Promise.all([
          tx.assignmentModel.deleteMany({
            where: {
              entityId: id,
              entityType: UserRoleAssignmentEntityType.SPONSOR,
            },
          }),
          tx.participantInvitation.deleteMany({
            where: { entityId: id, entityType: ParticipantType.SPONSOR },
          }),
          tx.formEntity.deleteMany({ where: { entityID: id } }),
          tx.networking.deleteMany({
            where: { entityId: id, entityModel: NetworkingModel.SPONSOR },
          }),
          tx.availability.deleteMany({
            where: { entityId: id, entityModel: AvailabilitiesModel.SPONSOR },
          }),
          tx.meetingParticipant.deleteMany({ where: { entityId: id } }),
          tx.message.deleteMany({ where: { senderId: id } }),
          tx.bookmark.deleteMany({ where: { entityId: id } }),
          tx.orderItem.updateMany({
            where: { sponsorId: id },
            data: { sponsorId: null },
          }),
          tx.entity.updateMany({
            where: { sponsorId: id },
            data: { sponsorId: null },
          }),
          tx.contactBookGroup.updateMany({
            where: { sponsorId: id },
            data: { sponsorId: null },
          }),
        ]);

        const connectionRequests = await tx.connectionRequest.findMany({
          where: {
            eventId: sponsor.eventId,
            OR: [{ senderId: id }, { receiverId: id }],
          },
          select: { id: true },
        });
        const connectionRequestIds = connectionRequests.map((r) => r.id);
        if (connectionRequestIds.length) {
          const notifications = await tx.notification.findMany({
            where: { entityId: { in: connectionRequestIds } },
            select: { id: true },
          });
          const notificationIds = notifications.map((n) => n.id);
          await Promise.all([
            notificationIds.length
              ? tx.notificationTranslations.deleteMany({
                  where: { notificationId: { in: notificationIds } },
                })
              : Promise.resolve(),
            notificationIds.length
              ? tx.notification.deleteMany({
                  where: { id: { in: notificationIds } },
                })
              : Promise.resolve(),
            tx.connectionRequest.deleteMany({
              where: { id: { in: connectionRequestIds } },
            }),
          ]);
        }

        if (taskIds.length) {
          await Promise.all([
            tx.formResultEntity.deleteMany({
              where: { taskId: { in: taskIds } },
            }),
            tx.taskTransition.deleteMany({
              where: { taskEntityId: { in: taskIds } },
            }),
            tx.taskEntity.deleteMany({ where: { entityID: id } }),
          ]);
        }

        await tx.sponsor.update({
          where: { id },
          data: { team: { set: [] }, translations: { deleteMany: {} } },
        });
        await tx.sponsor.delete({ where: { id } });
      },
      { timeout: 60_000, maxWait: 10_000 }
    );

    await invalidateEventPageCacheForEvent(sponsor.eventId);

    return true;
  } catch (error: any) {
    throw new Error(error?.message || "Failed to delete sponsor");
  }
};

export const addSponsorTeamMember = async (
  sponsorId: string,
  data: Prisma.UserCreateInput & {
    eventId: string;
    jobTitle?: string;
    qrReferenceNumber?: string;
    profileId?: string;
    permissions?: Prisma.ExhibitorPermissionsCreateInput;
    participantImportId?: string;
  }
) => {
  try {
    const contactBookGroup = await getContactBookGroupBySponsorId(sponsorId);
    const token = jwt.sign(
      {
        entityId: sponsorId,
        email: data.email as string,
        entityType: ParticipantGroupType.SPONSORS,
      },
      process.env.SECRET_HASH_PASSWORD as string
    );
    const {
      eventId,
      jobTitle,
      qrReferenceNumber,
      profileId,
      permissions,
      participantImportId,
      name,
      email,
      phoneNumber,
    } = data;
    const whiteLabel = await getWhiteLabelByEventId(eventId);
    const accessibleWhiteLabelId = whiteLabel?.id as string;
    const normalizedEmail = String(email).trim().toLowerCase();
    const [existingProfile, existingInvitation] = await Promise.all([
      prisma.profile.findFirst({
        where: {
          eventId,
          OR: [
            { email: normalizedEmail },
            { user: { email: normalizedEmail } },
          ],
        },
        select: { id: true },
      }),
      prisma.participantInvitation.findFirst({
        where: {
          entityId: sponsorId,
          email: normalizedEmail,
        },
        select: { id: true },
      }),
    ]);
    if (existingProfile || existingInvitation) {
      throw new Error(
        `Email "${normalizedEmail}" already belongs to a participant in this event`
      );
    }
    const response = await prisma.user.upsert({
      where: {
        email_accessibleWhiteLabelId: {
          email: normalizedEmail,
          accessibleWhiteLabelId,
        },
      },
      create: {
        name: name as string,
        email: normalizedEmail,
        phoneNumber: phoneNumber as string,
        accessibleWhiteLabelId,
        participantImportId,
        profiles: {
          create: {
            name: name as string,
            email: normalizedEmail,
            phoneNumber: phoneNumber as string,
            jobTitle: jobTitle as string,
            qrReferenceNumber,
            eventId,
            contactBookGroupId: contactBookGroup.id,
            exhibitorPermissions: permissions ?? undefined,
            participantImportId,
          },
        },
        sponsors: {
          connect: {
            id: sponsorId,
          },
        },
        userRoleAssignments: {
          create: {
            entityType: UserRoleAssignmentEntityType.SPONSOR,
            entityId: sponsorId,
          },
        },
      },
      update: {
        accessibleWhiteLabelId,
        name: name ?? "",
        email: normalizedEmail,
        phoneNumber: phoneNumber as string,
        sponsors: {
          connect: {
            id: sponsorId,
          },
        },
        profiles: profileId
          ? {
              upsert: {
                where: { id: profileId },
                create: {
                  eventId,
                  jobTitle: jobTitle as string,
                  qrReferenceNumber,
                  name: name ?? "",
                  email: normalizedEmail,
                  phoneNumber: phoneNumber as string,
                  contactBookGroupId: contactBookGroup.id,
                  exhibitorPermissions: permissions ?? undefined,
                  participantImportId,
                },
                update: {
                  eventId,
                  jobTitle: jobTitle as string,
                  qrReferenceNumber,
                  name: name ?? "",
                  email: normalizedEmail,
                  phoneNumber: phoneNumber as string,
                  contactBookGroupId: contactBookGroup.id,
                  exhibitorPermissions: permissions ?? undefined,
                  participantImportId,
                },
              },
            }
          : {
              create: {
                eventId,
                jobTitle: jobTitle as string,
                qrReferenceNumber,
                name: name ?? "",
                email: normalizedEmail,
                phoneNumber: phoneNumber as string,
                contactBookGroupId: contactBookGroup.id,
                exhibitorPermissions: permissions ?? undefined,
                participantImportId,
              },
            },
        userRoleAssignments: {
          create: {
            entityType: UserRoleAssignmentEntityType.SPONSOR,
            entityId: sponsorId,
          },
        },
      },
    });

    if (permissions) {
      await prisma.profile.updateMany({
        where: { userId: response.id, eventId },
        data: { exhibitorPermissions: permissions },
      });
    }

    await prisma.participantInvitation.upsert({
      where: {
        entityId_entityType_email: {
          entityId: sponsorId,
          email: normalizedEmail,
          entityType: ParticipantType.SPONSOR,
        },
      },
      update: {
        status: InvitationStatus.PENDING,
        token,
        participantImportId,
      },
      create: {
        email: normalizedEmail,
        status: InvitationStatus.PENDING,
        entityId: sponsorId,
        entityType: ParticipantType.SPONSOR,
        token,
        isOwner: false,
        participantImportId,
      },
    });

    return { ...response, token };
  } catch (error: any) {
    console.log(error);
    throw new Error(error.message);
  }
};

export const removeSponsorTeamMember = async (
  sponsorId: string,
  userId: string
) => {
  try {
    await assertNotOwnerTeamMember(sponsorId, ParticipantType.SPONSOR, userId);

    const response = await prisma.sponsor.update({
      where: {
        id: sponsorId,
      },
      data: {
        team: {
          disconnect: {
            id: userId,
          },
        },
        assignmentModels: {
          deleteMany: {
            userId: userId,
            entityType: UserRoleAssignmentEntityType.SPONSOR,
            entityId: sponsorId,
          },
        },
      },
    });
    return response;
  } catch (error: any) {
    console.log(error);
    throw new Error(error.message);
  }
};

export const updateSponsorInvitationStatus = async (
  sponsorId: string,
  email: string
) => {
  const token = jwt.sign(
    {
      entityId: sponsorId,
      email: email,
      entityType: ParticipantType.SPONSOR,
    },
    process.env.SECRET_HASH_PASSWORD as string
  );
  try {
    const response = await prisma.sponsor.update({
      where: {
        id: sponsorId,
      },
      data: {
        sponsorInvitations: {
          upsert: {
            where: {
              entityId_entityType_email: {
                entityId: sponsorId,
                entityType: ParticipantType.SPONSOR,
                email: email,
              },
            },
            create: {
              status: InvitationStatus.ACCEPTED,
              email: email,
              token: token,
              entityType: ParticipantType.SPONSOR,
            },
            update: {
              status: InvitationStatus.ACCEPTED,
            },
          },
        },
      },
    });
    return response;
  } catch (error: any) {
    console.log(error);
    throw new Error(error.message);
  }
};

export const isSponsorInvitationAccepted = async (
  sponsorId: string,
  email: string
) => {
  const sponsor = await prisma.sponsor.findUnique({
    where: {
      id: sponsorId,
    },
    include: {
      sponsorInvitations: true,
    },
  });
  return {
    isSponsorExisted: !!sponsor,
    accepted:
      sponsor?.sponsorInvitations.find((inv) => inv.email === email)?.status ===
      InvitationStatus.ACCEPTED,
  };
};

export const updateSponsorSendAtTime = async (id: string, sendAt: Date) => {
  try {
    const response = await prisma.sponsor.update({
      where: {
        id,
      },
      data: {
        lastReminderSentAt: sendAt,
      },
    });
    return response;
  } catch (error: any) {
    console.log(error);
    throw new Error(error.message);
  }
};

export const getSponsorUploads = async (sponsorId: string) => {
  const sponsor = await prisma.sponsor.findUnique({
    where: {
      id: sponsorId,
    },
    include: {
      translations: {
        where: {
          language: {
            code: "en",
          },
        },
      },
      taskEntities: {
        include: {
          taskTransitions: true,
          formResults: {
            include: {
              formResult: true,
            },
          },
          task: {
            include: {
              taskTranslations: {
                where: {
                  language: {
                    code: "en",
                  },
                },
              },
              formEntity: {
                include: {
                  formTemplate: true,
                },
              },
            },
          },
        },
      },
    },
  });
  const uploads =
    sponsor?.taskEntities?.map((taskEntity) => {
      return taskEntity.uploadedFiles?.map((file) => {
        return {
          url: file.url,
          fileName: `${file.key}`,
          key: file.key,
        };
      });
    }) || [];

  return {
    files: uploads.flat(),
    name: sponsor?.translations?.[0]?.name,
    tasks:
      sponsor?.taskEntities?.map((taskEntity) => {
        return {
          name: taskEntity.task.taskTranslations?.[0]?.name,
          timeSubmitted:
            taskEntity.taskTransitions[taskEntity.taskTransitions.length - 1]
              ?.createdAt,
          files: taskEntity.uploadedFiles,
          type: taskEntity.task.type,
          results: taskEntity.formResults[0]?.formResult?.surveyResult
            ? transformSurveyResult(
                taskEntity.task.formEntity?.formTemplate?.json || {},
                taskEntity.formResults[0]?.formResult?.surveyResult
              )
            : undefined,
        };
      }) || [],
  };
};

export const getSponsorsSubmitedData = async (eventId: string) => {
  const sponsor = await prisma.sponsor.findMany({
    where: {
      eventId: eventId,
    },
    include: {
      translations: {
        where: {
          language: {
            code: "en",
          },
        },
      },
      taskEntities: {
        include: {
          taskTransitions: true,
          formResults: {
            include: {
              formResult: true,
            },
          },
          task: {
            include: {
              taskTranslations: {
                where: {
                  language: {
                    code: "en",
                  },
                },
              },
              formEntity: {
                include: {
                  formTemplate: {
                    select: {
                      id: true,
                      json: true,
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
  });
  const data = sponsor.map((sponsor) => {
    const uploads =
      sponsor?.taskEntities?.map((taskEntity) => {
        return taskEntity.uploadedFiles?.map((file) => {
          return {
            url: file.url,
            fileName: `${file.key}`,
            key: file.key,
          };
        });
      }) || [];

    return {
      files: uploads.flat(),
      name: sponsor?.translations?.[0]?.name,
      tasks:
        sponsor?.taskEntities?.map((taskEntity) => {
          return {
            name: taskEntity.task.taskTranslations?.[0]?.name,
            files: taskEntity.uploadedFiles,
            type: taskEntity.task.type,
            timeSubmitted:
              taskEntity.taskTransitions[taskEntity.taskTransitions.length - 1]
                ?.createdAt,
            results: taskEntity.formResults[0]?.formResult?.surveyResult
              ? transformSurveyResult(
                  taskEntity.task.formEntity?.formTemplate?.json || {},
                  taskEntity.formResults[0]?.formResult?.surveyResult
                )
              : undefined,
          };
        }) || [],
    };
  });
  return data;
};

export const exportSponsorsList = async (eventId: string, lang?: string) => {
  try {
    // Fetch exhibitors from the database
    const sponsors = (await prisma.sponsor.findMany({
      // @ts-ignore
      where: {
        teamIds: { isEmpty: false },
        eventId,
      },
      include: {
        participantGroup: {
          include: {
            translations: {
              include: {
                language: true,
              },
            },
          },
        },
        taskEntities: {
          include: {
            formResults: {
              include: {
                formResult: true,
              },
            },
            taskTransitions: true,
            task: {
              include: {
                formEntity: {
                  include: {
                    formTemplate: true,
                  },
                },
                taskTranslations: {
                  where: {
                    language: {
                      code: lang,
                    },
                  },
                },
              },
            },
          },
        },
        sponsorInvitations: true,
        translations: lang
          ? {
              where: {
                language: {
                  code: lang,
                },
              },
            }
          : true,
      },
      orderBy: {
        createdAt: "desc", // Order by creation date or any other field
      },
    })) as any;
    // Return the data table with pagination info
    return {
      result: sponsors?.map((sponsor: any) => {
        const groupName =
          sponsor.participantGroup?.translations?.find(
            (item: any) => item.language?.code === lang
          )?.name ?? sponsor.participantGroup?.translations?.[0]?.name;

        return {
          ...sponsor,
          name: sponsor.translations?.[0]?.name,
          bio: sponsor.translations?.[0]?.bio,
          participantGroup: sponsor.participantGroup
            ? { ...sponsor.participantGroup, name: groupName }
            : null,

          completedTasksPercentage: {
            total: sponsor?.taskEntities?.length,
            completed: sponsor?.taskEntities?.filter(
              (task: any) =>
                task.taskTransitions[task.taskTransitions.length - 1]
                  ?.toState === TaskTransitionType.COMPLETED ||
                task.taskTransitions[task.taskTransitions.length - 1]
                  ?.toState === TaskTransitionType.SUBMITTED
            )?.length,
          },
          taskEntity: sponsor?.taskEntities?.map((task: any) => {
            return {
              ...task,
              formResult: task?.formResults[0]?.formResult
                ? {
                    ...task.formResults[0].formResult,
                    surveyResult: transformSurveyResult(
                      task.task.formEntity.formTemplate.json,
                      task.formResults[0].formResult.surveyResult
                    ),
                  }
                : null,
              task: {
                ...task.task,
                name: task.task.taskTranslations?.[0]?.name,
              },
            };
          }),
          hasUploadedFiles: sponsor?.taskEntities?.some(
            (task: any) =>
              task?.uploadedFiles?.length > 0 || task?.formResult?.formResult
          ),
        };
      }),
    };
  } catch (error: any) {
    console.error(error);
    throw new Error(error.message);
  }
};

export const updateSponsorTeamMember = async (
  id: string,
  updatedData: Prisma.UserUpdateInput & {
    jobTitle?: string;
    profileId?: string;
    permissions?: Prisma.ExhibitorPermissionsCreateInput;
  },
  eventId: string,
  sponsorId: string
) => {
  const contactBookGroup = await getContactBookGroupBySponsorId(sponsorId);
  return updatePortalTeamMember({
    userId: id,
    entityId: sponsorId,
    entityType: ParticipantType.SPONSOR,
    eventId,
    contactBookGroupId: contactBookGroup.id,
    updatedData: {
      name: updatedData.name as string | undefined,
      email: updatedData.email as string | undefined,
      jobTitle: updatedData.jobTitle,
      phoneNumber: updatedData.phoneNumber as string | undefined,
      profileId: updatedData.profileId,
      permissions: updatedData.permissions,
    },
  });
};
//     return user;
//   } catch (error) {
//     console.error(error);
//     throw error;
//   }
// };

export const getSponsorByEventIdAndName = async (
  name: string,
  eventId: string
) => {
  if (!name) {
    return null;
  }
  return await prisma.sponsor.findFirst({
    where: {
      eventId,
      translations: {
        some: {
          name,
        },
      },
    },
  });
};
